import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X, Bell, RefreshCw, CheckCircle } from 'lucide-react';
import {
  fetchSiniestAlertas, marcarAlertasLeidas, fetchAlertasConfig, saveAlertasConfig, fetchCorrelacion,
} from '@/services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useUserStore } from '@/store/userStore';
import { toast } from 'sonner';

export function AlertasPanel({ open, onClose }) {
  const { user } = useUserStore();
  const canAdmin = user && ['superadmin', 'admin'].includes(user.role);
  const qc = useQueryClient();
  const [tab, setTab] = useState('alertas');
  const [thresholds, setThresholds] = useState([]);
  const [newGerente, setNewGerente] = useState('');
  const [newUmbral, setNewUmbral] = useState(50);

  const { data: alertas = [], isLoading: loadingAlertas } = useQuery({
    queryKey: ['siniest-alertas'],
    queryFn: fetchSiniestAlertas,
    enabled: open,
  });

  const { data: config = [], isLoading: loadingConfig } = useQuery({
    queryKey: ['siniest-alertas-config'],
    queryFn: fetchAlertasConfig,
    enabled: open && canAdmin,
    onSuccess: (data) => setThresholds(data.map(d => ({ ...d }))),
  });

  const { data: correlData = [] } = useQuery({
    queryKey: ['correlacion'],
    queryFn: fetchCorrelacion,
    enabled: open && canAdmin && tab === 'config',
    staleTime: 120000,
  });
  const availableGerentes = correlData
    .map(r => r.gerente)
    .filter(g => g && g !== 'Sin Gerente' && !thresholds.find(t => t.gerente_zona === g));

  const markRead = useMutation({
    mutationFn: marcarAlertasLeidas,
    onSuccess: () => {
      toast.success('Alertas marcadas como leídas');
      qc.invalidateQueries({ queryKey: ['siniest-alertas'] });
      qc.invalidateQueries({ queryKey: ['siniest-alertas-count'] });
    },
  });

  const saveConfig = useMutation({
    mutationFn: () => saveAlertasConfig(thresholds),
    onSuccess: () => {
      toast.success('Umbrales guardados');
      qc.invalidateQueries({ queryKey: ['siniest-alertas-config'] });
    },
    onError: () => toast.error('Error al guardar umbrales'),
  });

  const handleThresholdChange = (i, field, val) => {
    setThresholds(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: val } : t));
  };

  const handleAddThreshold = () => {
    const g = newGerente.trim();
    if (!g) { toast.error('Introduce el nombre del gerente'); return; }
    if (thresholds.find(t => t.gerente_zona === g)) { toast.error('Ya existe un umbral para este gerente'); return; }
    setThresholds(prev => [...prev, { gerente_zona: g, umbral_if: newUmbral, activo: true }]);
    setNewGerente('');
    setNewUmbral(50);
  };

  const handleRemoveThreshold = (i) => {
    setThresholds(prev => prev.filter((_, idx) => idx !== i));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl"
        style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
        data-testid="alertas-panel"
      >
        <DialogHeader>
          <DialogTitle className="font-montserrat flex items-center gap-2" style={{ color: 'var(--vz-text-primary)' }}>
            <Bell size={18} style={{ color: '#EF5350' }} />
            Alertas de Siniestralidad
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 border-b" style={{ borderColor: 'var(--vz-border-subtle)' }}>
          <button
            onClick={() => setTab('alertas')}
            className="px-4 py-2 text-xs font-semibold transition-colors"
            style={{
              borderBottom: tab === 'alertas' ? '2px solid var(--vz-green)' : '2px solid transparent',
              color: tab === 'alertas' ? 'var(--vz-green)' : 'var(--vz-text-muted)',
            }}
            data-testid="alertas-tab-alertas"
          >
            Alertas activas {alertas.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold" style={{ background: 'rgba(239,83,80,0.15)', color: '#EF5350' }}>
                {alertas.length}
              </span>
            )}
          </button>
          {canAdmin && (
            <button
              onClick={() => setTab('config')}
              className="px-4 py-2 text-xs font-semibold transition-colors"
              style={{
                borderBottom: tab === 'config' ? '2px solid var(--vz-green)' : '2px solid transparent',
                color: tab === 'config' ? 'var(--vz-green)' : 'var(--vz-text-muted)',
              }}
              data-testid="alertas-tab-config"
            >
              Configurar umbrales
            </button>
          )}
        </div>

        {/* Alertas tab */}
        {tab === 'alertas' && (
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
            {loadingAlertas ? (
              <div className="flex items-center justify-center py-10">
                <RefreshCw size={20} className="animate-spin" style={{ color: 'var(--vz-green)' }} />
              </div>
            ) : alertas.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <CheckCircle size={32} style={{ color: 'var(--vz-green)' }} />
                <p className="text-sm" style={{ color: 'var(--vz-text-muted)' }}>No hay alertas activas</p>
                <p className="text-xs text-center" style={{ color: 'var(--vz-text-muted)' }}>
                  Las alertas aparecen cuando el IF de un gerente supera el umbral configurado
                </p>
              </div>
            ) : (
              <>
                {alertas.map((a, i) => (
                  <div
                    key={i}
                    className="rounded-xl p-3 flex items-start gap-3"
                    style={{ background: 'rgba(239,83,80,0.06)', border: '1px solid rgba(239,83,80,0.2)' }}
                    data-testid="alerta-item"
                  >
                    <AlertTriangle size={16} style={{ color: '#EF5350', marginTop: 2, flexShrink: 0 }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--vz-text-primary)' }}>
                        {a.gerente_zona}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                        IF actual: <span style={{ color: '#EF5350', fontWeight: 700 }}>{a.if_actual}</span>
                        {' · '}Umbral: <span style={{ color: 'var(--vz-orange)' }}>{a.umbral_if}</span>
                        {' · '}Exceso: <span style={{ color: '#EF5350' }}>+{a.exceso}</span>
                      </p>
                      {a.fecha && (
                        <p className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                          {new Date(a.fecha).toLocaleString('es-ES')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                <Button
                  size="sm"
                  className="w-full gap-2"
                  style={{ background: 'rgba(0,153,64,0.1)', border: '1px solid rgba(0,153,64,0.25)', color: 'var(--vz-green)' }}
                  onClick={() => markRead.mutate()}
                  disabled={markRead.isPending}
                  data-testid="alertas-mark-read-btn"
                >
                  <CheckCircle size={13} />
                  {markRead.isPending ? 'Marcando...' : 'Marcar todas como leídas'}
                </Button>
              </>
            )}
          </div>
        )}

        {/* Config tab (admin only) */}
        {tab === 'config' && canAdmin && (
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
            {loadingConfig ? (
              <div className="flex items-center justify-center py-10">
                <RefreshCw size={20} className="animate-spin" style={{ color: 'var(--vz-green)' }} />
              </div>
            ) : (
              <>
                <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>
                  Configura el umbral de IF (Índice de Frecuencia) por gerente de zona. Se generará una alerta tras cada importación cuando el IF supere el umbral.
                </p>

                <div
                  className="rounded-xl p-3 space-y-2"
                  style={{ background: 'rgba(0,153,64,0.05)', border: '1px solid rgba(0,153,64,0.2)' }}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--vz-green)' }}>Añadir umbral</p>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Input
                        list="gerentes-datalist"
                        value={newGerente}
                        onChange={e => setNewGerente(e.target.value)}
                        placeholder="Nombre gerente de zona..."
                        className="h-7 text-xs"
                        style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
                        data-testid="new-threshold-gerente"
                      />
                      <datalist id="gerentes-datalist">
                        {availableGerentes.map(g => <option key={g} value={g} />)}
                      </datalist>
                    </div>
                    <Input
                      type="number"
                      value={newUmbral}
                      onChange={e => setNewUmbral(parseFloat(e.target.value) || 0)}
                      placeholder="IF"
                      className="w-20 h-7 text-xs text-right"
                      style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
                      data-testid="new-threshold-umbral"
                    />
                    <Button
                      size="sm"
                      className="h-7 px-3 text-xs gap-1"
                      style={{ background: 'var(--vz-green)', color: '#fff' }}
                      onClick={handleAddThreshold}
                      data-testid="add-threshold-btn"
                    >
                      +
                    </Button>
                  </div>
                </div>

                {thresholds.length === 0 ? (
                  <p className="text-xs text-center py-4" style={{ color: 'var(--vz-text-muted)' }}>
                    Sin umbrales configurados. Añade uno arriba.
                  </p>
                ) : thresholds.map((t, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                    style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate" style={{ color: 'var(--vz-text-primary)' }}>{t.gerente_zona}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <label className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>IF:</label>
                      <Input
                        type="number"
                        value={t.umbral_if ?? 50}
                        onChange={(e) => handleThresholdChange(i, 'umbral_if', parseFloat(e.target.value) || 0)}
                        className="w-20 h-7 text-xs text-right"
                        style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
                      />
                      <button
                        onClick={() => handleThresholdChange(i, 'activo', !t.activo)}
                        className="text-[10px] px-2 py-1 rounded-lg transition-colors"
                        style={{
                          background: t.activo ? 'rgba(0,153,64,0.12)' : 'rgba(100,116,139,0.12)',
                          color: t.activo ? 'var(--vz-green)' : 'var(--vz-text-muted)',
                          border: `1px solid ${t.activo ? 'rgba(0,153,64,0.25)' : 'rgba(100,116,139,0.2)'}`,
                        }}
                      >
                        {t.activo ? 'Activo' : 'Inactivo'}
                      </button>
                      <button
                        onClick={() => handleRemoveThreshold(i)}
                        className="w-6 h-6 rounded-lg flex items-center justify-center transition-colors"
                        style={{ background: 'rgba(239,83,80,0.1)', color: '#EF5350', border: '1px solid rgba(239,83,80,0.2)' }}
                        data-testid="remove-threshold-btn"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  </div>
                ))}

                {thresholds.length > 0 && (
                  <Button
                    size="sm"
                    className="w-full gap-2"
                    style={{ background: 'var(--vz-green)', color: '#fff' }}
                    onClick={() => saveConfig.mutate()}
                    disabled={saveConfig.isPending}
                    data-testid="alertas-save-config-btn"
                  >
                    {saveConfig.isPending ? 'Guardando...' : `Guardar ${thresholds.length} umbral${thresholds.length !== 1 ? 'es' : ''}`}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
